<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses]"
  >
    <Loading v-if="progressLoading > 0" />
    <component
      :is="layout"
      v-if="isLoaded">
      <router-view />
    </component>
    <zoom-embed-widget
      v-if="showZoomEmbed"
    />
  </div>
</template>

<script>

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { mapActions, mapGetters } from 'vuex'
import ToastificationContentTicket from '@/components/ToastificationContentTicket.vue'
import { provideToast } from 'vue-toastification/composition'
import { watch } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
// import TicketProvider from '@/resources/TicketProvider'
// import returnDataTicket from '@/utils/data-ticket'
import Loading from '@/components/Loading.vue'
import ZoomEmbedWidget from '@/components/ZoomEmbed/ZoomEmbedWidget.vue'
import { isValidToken } from './auth/utils'

import { useWindowSize, useCssVar } from '@vueuse/core'

import store from '@/store'
import AuthProvider from '@/resources/AuthProvider'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import SocketioService from './services/socketio.service'
import {
  startNexusSeatKeepAlive,
  stopNexusSeatKeepAlive,
  NEXUS_SEAT_FAILURE_EVENT
} from './services/nexusSeatKeepAlive'
import { getCXOneMessageType } from '@/utils/cxoneAuthMessage'
import { showErrorToast } from '@/utils/toast'
import { Logger } from '@/utils/logger'

const appLogger = Logger.create('App')

// const TicketService = new TicketProvider()

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')
const AuthService = new AuthProvider()
export default {
  components: {
    Loading,
    ZoomEmbedWidget,
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull

  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  // provide () {
  //   // use function syntax so that we can access `this`
  //   return {
  //     profileData: this.profile
  //   }
  // },
  data () {
    return {
      isLoaded: true
    }
  },
  computed: {
    layout () {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType () {
      return this.$store.state.appConfig.layout.type
    },
    showZoomEmbed () {
      return this.isLoaded
        && isValidToken()
        && this.$route.meta.layout !== 'full'
        && Boolean(process.env.VUE_APP_ZOOM_EMBED_URL)
        && Boolean(this.profile?.zoomEnabled)
    },
    ...mapGetters({
      // map `this.doneCount` to `this.$store.getters.doneTodosCount`
      profile: 'User/user',
      progressLoading: 'activity/progressLoading',
      isTubtimSso: 'User/isTubtimSso',
      accessToken: 'User/accessToken'
    })
  },
  watch: {
    isTubtimSso: {
      immediate: true,
      handler (enabled) {
        if (enabled && this.accessToken) {
          startNexusSeatKeepAlive()
        } else {
          stopNexusSeatKeepAlive()
        }
      }
    },
    accessToken (token) {
      if (this.isTubtimSso && token) {
        startNexusSeatKeepAlive()
      } else if (!token) {
        stopNexusSeatKeepAlive()
      }
    },
    // Connect once profile is known — prefer watch over setInterval poll
    'profile.id': {
      immediate: true,
      handler (id) {
        if (id) this.connectSocket(id)
      }
    }
  },
  beforeCreate () {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  setup () {
    const { skin, skinClasses } = useAppConfig()

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: true,
      closeButton: true,
      icon: false,
      timeout: 3600000,
      transition: 'Vue-Toastification__fade'
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, (val) => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses
    }
  },
  beforeDestroy () {
    window.removeEventListener(NEXUS_SEAT_FAILURE_EVENT, this.onNexusSeatKeepAliveFailed)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    stopNexusSeatKeepAlive()
  },
  async mounted () {
    this.isLoaded = false
    this.observeMax()
    window.addEventListener(NEXUS_SEAT_FAILURE_EVENT, this.onNexusSeatKeepAliveFailed)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
    this.isLoaded = true
  },
  methods: {
    ...mapActions({
      setUser: 'User/setUser'
    }),
    onNexusSeatKeepAliveFailed (event) {
      const message = event?.detail?.message
        || 'Could not keep your Nexus seat active. Please sign in again if licensing fails.'
      showErrorToast(this, 'Nexus seat', message)
    },
    onVisibilityChange () {
      // Reuse same Socket.IO instance after background-tab kill (skills/socket-io-reuse-instance-on-tab-resume)
      if (document.visibilityState === 'visible' && this.profile?.id) {
        SocketioService.resumeIfNeeded()
      }
    },
    async signInCXOne (sessionToken) {
      try {
        const { data } = await AuthService.signInCXOne(sessionToken)
        if (data?.token) {
          this.setUser({
            ...data?.user,
            token: data?.token
          })
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Login Success',
              icon: 'EditIcon',
              variant: 'success'
            }
          })
          this.$router.go(0)
        }
      } catch (error) {
        appLogger.error('SignInCXOne error:', error)
      }
    },
    async observeMax () {
      // Find the parent window (MAX) to register for events
      appLogger.debug('Running Post Message... RegisterForClientEvents')
      const opener = window.opener || window.parent

      // Set up your subscriptions
      const subscriptionTypes = ['sessioninfo']

      // Start listening for response messages
      const login = () => {
        const clientId = 'a9496592-61de-4190-8c6c-a47c711369b2'
        const redirectUri = `${process.env.VUE_APP_URL || 'https://brs.tubtimcrm.com'}/cxone/auth`
        const authUrl = `https://cxone.niceincontact.com/auth/authorize?scope=openid&client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}`
        
        // เปิด popup window แทน redirect
        const popup = window.open(
          authUrl,
          'CXOneAuth',
          'width=600,height=700,left=200,top=100,menubar=no,toolbar=no,location=no,status=no'
        )
        
        // เช็คว่า popup ถูก block หรือไม่
        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Popup Blocked',
              text: 'Please allow popups for this website to login with CXone',
              icon: 'AlertCircleIcon',
              variant: 'warning'
            }
          })
          return
        }
        
        // Monitor popup - เช็คว่าปิดแล้วหรือยัง
        const checkPopup = setInterval(() => {
          if (!popup || popup.closed) {
            clearInterval(checkPopup)
            // เช็คว่า login สำเร็จหรือไม่
            const isLoggedIn = isValidToken()
            if (isLoggedIn) {
              appLogger.info('Login successful, reloading page...')
              // reload เพื่อ refresh state
              this.$router.go(0)
            }
          }
        }, 500)
      }

      const listenForPostMessage = async (event) => {
        const isLoginPage = this.$route.name === 'login'
        const cxoneType = getCXOneMessageType(event, window.location.origin)

        if (cxoneType === 'success') {
          if (isLoginPage) return
          appLogger.info('Auto login from CXOne, applying session and redirecting to dashboard...')
          this.$store.dispatch('User/applyCXOneAuthPayload', event.data)
          this.$router.push({ name: 'dashboard' })
          return
        }
        if (cxoneType === 'error') {
          if (isLoginPage) return
          appLogger.error('CXOne auth error:', event.data?.error)
          showErrorToast(this, 'Login Failed', event.data?.error || 'Authentication failed')
          return
        }

        // รับ message จาก CXone MAX
        if (
          event.data
          && event.data.events
          && event.data.issuer === 'MAX'
        ) {
          const isLoggedIn = isValidToken()
          appLogger.debug('MAX event received - isLoggedIn:', isLoggedIn, 'event:', event)
          const data = event?.data?.events
          if (Array.isArray(data) && data.length > 0) {
            let sessionToken = data[1]?.sessionToken
            data.forEach((item) => {
              if (item?.sessionToken) {
                sessionToken = item?.sessionToken
              }
            })
            appLogger.debug('sessionToken:', sessionToken)
            if (sessionToken) {
              appLogger.debug('SET SESSION:', this.$store.getters['User/sessionId'])
              this.$store.dispatch('User/setSessionId', { sessionId: sessionToken })
              appLogger.debug('SET AFTER SESSION:', sessionToken, this.$store.getters['User/sessionId'])
            }
          }
          // const tokenExpired = this.$store.getters['User/tokenExpired']
          // const timeNow = Math.floor(new Date().getTime() / 1000)
          // if ((timeNow >= tokenExpired) || !tokenExpired) {
          //   login(event?.data?.events)
          // }
          if(!isLoggedIn) {
            login()
          }
        }
      }

      // Add the listener for MAX client events.
      window.addEventListener('message', listenForPostMessage)

      // Send the registration message to MAX
      setTimeout(() => {
        opener.postMessage(
          {
            issuer: 'Tubtim',
            messageType: 'RegisterForClientEvents',
            subscriptionTypes
          },
          '*'
        )
        appLogger.debug('End Post Message...')
      }, 100)
    },
    async connectSocket (profileId) {
      // Singleton + bind once; Socket.IO reconnection handles drops (no second io()).
      SocketioService.ensureAgentChannel(profileId, {
        onConnect: () => {
          appLogger.debug('SOCKET CONNECT')
        },
        onDisconnect: (reason) => {
          appLogger.warn('SOCKET disconnect', reason)
        },
        onAgentEvent: async (payload) => {
          if (payload?.success) {
            store.dispatch('ticket/ADD_TICKET', {
              ...payload?.ticket
            })
            this.$toast({
              component: ToastificationContentTicket,
              listeners: {
                click: () => {
                  this.$router.push('/ticket/add')
                }
              },
              props: {
                title: payload?.ani || payload?.transactionId || '',
                closeOnClick: true,
                timeout: 3600000,
                icon: payload?.type === 'non-voice' ? 'MessageSquareIcon' : 'PhoneIncomingIcon',
                variant: 'success',
                text: payload?.skillName || payload?.contactChannel || '',
                hideClose: false,
                customerName: payload?.ticket?.customerName,
                contactName: payload?.ticket?.contactName,
                isHaveContact: payload?.isHaveContact,
                customerGroup: payload?.ticket?.customerProfile?.customerGroup?.name || null,
                notificationColor: payload?.ticket?.customerProfile?.customerGroup?.notificationColor || null
              }
            })
          }
        }
      })
    },
    retry (func, retries, delay) {
      let retriesTime = retries
      return new Promise((resolve, reject) => {
        const attempt = () => {
          func()
            .then(resolve)
            .catch((error) => {
              if (retriesTime > 0) {
                setTimeout(() => {
                  retriesTime--
                  attempt()
                }, delay)
              } else {
                reject(error)
              }
            })
        }
        attempt()
      })
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

div, label {
  color: black !important;
}

</style>
